import { Link } from "react-router-dom";
import { mainSiteUrl } from "@/mocks/categories";
import { useEffect, type ReactNode } from "react";

function ArticleNavbar() {
  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-md border-b border-background-200/70">
      <div className="w-full px-4 md:px-10 py-3 flex items-center justify-between">
        <Link to="/" className="font-heading text-base md:text-xl font-bold text-foreground-950 tracking-tight whitespace-nowrap cursor-pointer">
          Mulebuy Spreadsheets
        </Link>
        <div className="flex items-center gap-3 md:gap-5">
          <Link to="/" className="text-xs md:text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap">
            <span className="w-3 h-3 md:w-4 md:h-4 inline-flex items-center justify-center mr-1"><i className="ri-home-line"></i></span>
            Home
          </Link>
          <Link to="/mulebuy-spreadsheet-guide" className="text-xs md:text-sm font-medium text-foreground-600 hover:text-foreground-900 transition-colors cursor-pointer whitespace-nowrap">
            Shopping Guide
          </Link>
          <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="text-xs md:text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap">
            Buy It Now
          </a>
        </div>
      </div>
    </nav>
  );
}

function ArticleFooter() {
  return (
    <footer className="w-full bg-background-100 border-t border-background-200/70 py-10">
      <div className="px-6 md:px-10 lg:px-16">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <div>
            <p className="text-sm text-foreground-500">
              <Link to="/" className="text-foreground-800 font-semibold hover:text-foreground-950 transition-colors cursor-pointer">
                &larr; Back to Mulebuy Spreadsheet Home
              </Link>
            </p>
            <p className="text-xs text-foreground-400 mt-2">
              Explore more <strong>mulebuy spreadsheet</strong> guides and resources on our main hub.
            </p>
          </div>
          <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-1 px-5 py-2 bg-foreground-900 text-background-50 rounded-md text-sm font-semibold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
            Mulebuy Spreadsheet
            <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line"></i></span>
          </a>
        </div>
        <p className="text-xs text-foreground-400 mt-6 pt-4 border-t border-background-200/70 text-center">
          &copy; {new Date().getFullYear()} Mulebuy Spreadsheets. This is an informational SEO resource.
        </p>
      </div>
    </footer>
  );
}

export default function ArticleLayout({ children, heroImage, heroAlt, tags, title, excerpt }: {
  children: ReactNode;
  heroImage: string;
  heroAlt: string;
  tags: { label: string; color: string }[];
  title: string;
  excerpt: string;
}) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const location = useLocation();
  const basePath = __BASE_PATH__ === "/" ? "" : __BASE_PATH__;
  const articleUrl = `${__VITE_SITE_URL__}${basePath}${location.pathname}`;

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": title,
    "description": excerpt,
    "image": heroImage,
    "url": articleUrl,
    "datePublished": "2026-06-22",
    "dateModified": "2026-06-22",
    "author": {
      "@type": "Organization",
      "name": "Mulebuy Spreadsheets",
      "url": __VITE_SITE_URL__
    },
    "publisher": {
      "@type": "Organization",
      "name": "Mulebuy Spreadsheets",
      "url": __VITE_SITE_URL__
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": articleUrl
    }
  };

  return (
    <main className="min-h-screen bg-background-50">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleJsonLd) }}
      />
      <ArticleNavbar />

      <article className="pt-16 md:pt-20 pb-16">
        <div className="px-6 md:px-10 lg:px-16 max-w-3xl mx-auto">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              {tags.map((tag) => (
                <span key={tag.label} className={`px-3 py-1 ${tag.color} rounded-full text-xs font-medium whitespace-nowrap`}>
                  {tag.label}
                </span>
              ))}
              <span className="text-xs text-foreground-400">June 2026</span>
            </div>

            <h1 className="font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-[1.1] tracking-tight">
              {title}
            </h1>

            <p className="mt-4 text-sm md:text-base text-foreground-500 leading-relaxed">
              {excerpt}
            </p>
          </div>

          <div className="w-full aspect-[16/7] rounded-xl overflow-hidden mb-10">
            <img src={heroImage} alt={heroAlt} className="w-full h-full object-cover object-top" />
          </div>

          <div className="prose-custom">
            {children}

            <section className="border-t border-background-200/70 pt-8 mt-10">
              <div className="bg-primary-50 rounded-xl p-6 md:p-8 text-center">
                <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-3">
                  Ready to Start Using Mulebuy Spreadsheet?
                </h3>
                <p className="text-sm md:text-base text-foreground-600 mb-5 max-w-md mx-auto">
                  Access the full platform and discover thousands of products organized with the
                  <strong className="text-foreground-900"> mulebuy spreadsheet</strong> tracking system.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                  <a href={mainSiteUrl} target="_blank" rel="noopener nofollow" className="inline-flex items-center gap-2 px-6 py-3 bg-foreground-900 text-background-50 rounded-md font-semibold text-sm hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
                    Mulebuy Spreadsheet
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-up-line"></i></span>
                  </a>
                  <Link to="/" className="inline-flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap">
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-home-line"></i></span>
                    Back to Homepage
                  </Link>
                </div>
              </div>
            </section>

            <section className="mt-10 pt-6 border-t border-background-200/70">
              <h4 className="font-heading text-sm font-bold text-foreground-500 uppercase tracking-wider mb-3">Related Articles</h4>
              <div className="flex flex-wrap gap-3">
                <Link to="/mulebuy-spreadsheet-guide" className="text-sm text-primary-700 hover:text-primary-800 transition-colors cursor-pointer border-b border-primary-300 hover:border-primary-600 pb-0.5 whitespace-nowrap">Beginner Guide</Link>
                <Link to="/mulebuy-spreadsheet-tools" className="text-sm text-primary-700 hover:text-primary-800 transition-colors cursor-pointer border-b border-primary-300 hover:border-primary-600 pb-0.5 whitespace-nowrap">Best Tools</Link>
                <Link to="/mulebuy-spreadsheet-comparison" className="text-sm text-primary-700 hover:text-primary-800 transition-colors cursor-pointer border-b border-primary-300 hover:border-primary-600 pb-0.5 whitespace-nowrap">Comparison</Link>
                <Link to="/" className="text-sm text-primary-700 hover:text-primary-800 transition-colors cursor-pointer border-b border-primary-300 hover:border-primary-600 pb-0.5 whitespace-nowrap">Mulebuy Spreadsheet Hub</Link>
              </div>
            </section>
          </div>
        </div>
      </article>

      <ArticleFooter />
    </main>
  );
}